import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import kubernetesHero from "../assets/Hero/kuber.webp"; // Replace with your hero image
import kubernetesImage from "../assets/Hero/kubs.jpeg"; // Replace with your Kubernetes image
import featuresImage from "../assets/Hero/Nairobi.jpg"; // Replace with your features image
import "../assets/css/kubernetesSetupPage.css";

// Register the ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// JSON data for headings and paragraphs
const kubernetesData = {
  hero: {
    title: "Kubernetes Setup Solutions",
    subtitle: "Empowering Your Business with Scalable and Efficient Container Orchestration",
  },
  introduction: {
    heading: "Why Kubernetes Setup Matters",
    paragraphs: [
      "Kubernetes is the leading platform for managing containerized applications, providing scalability, resilience, and automation.",
      "Our Kubernetes setup solutions are designed to simplify deployment, enhance performance, and ensure seamless management of your containerized workloads.",
    ],
  },
  features: {
    heading: "Our Kubernetes Setup Features",
    items: [
      "Automated Cluster Deployment",
      "Scalable Container Orchestration",
      "High Availability & Fault Tolerance",
      "Integrated Monitoring & Logging",
      "Seamless CI/CD Integration",
      "24/7 Expert Support",
    ],
  },
  benefits: {
    heading: "Benefits of Our Solutions",
    items: [
      {
        icon: "fa fa-cogs",
        text: "Simplified deployment and management of containerized applications.",
      },
      {
        icon: "fa fa-shield",
        text: "Enhanced security with role-based access control (RBAC).",
      },
      {
        icon: "fa fa-line-chart",
        text: "Scalable solutions to handle growing workloads.",
      },
      {
        icon: "fa fa-check-circle",
        text: "Compliance with industry standards and best practices.",
      },
      {
        icon: "fa fa-headphones",
        text: "Expert support for seamless Kubernetes operations.",
      },
    ],
  },
  cta: {
    heading: "Ready to Optimize Your Container Workloads?",
    text: "Contact us today to learn more about our Kubernetes Setup Solutions and how we can help your business thrive.",
  },
};

export const KubernetesSetupPage = ({ data }) => {
  const kubernetesRef = useRef(null);
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const elements = kubernetesRef.current.querySelectorAll(".kubernetes-animate");

    gsap.fromTo(
      elements,
      { y: 100, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1.0,
        ease: "power3.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: kubernetesRef.current,
          start: "top 80%",
          toggleActions: "play none none reset",
        },
      }
    );
  }, []);

  // Function to handle navigation
  const handleContactClick = () => {
    navigate("/contact"); // Navigate to the contact page
  };

  return (
    <div id="kubernetes-page" ref={kubernetesRef}>
      {/* Hero Section */}
      <div
        className="kubernetes-hero-section"
        style={{ backgroundImage: `url(${kubernetesHero})` }}
      >
        <h1 className="kubernetes-animate">{kubernetesData.hero.title}</h1>
        <p className="kubernetes-animate">{kubernetesData.hero.subtitle}</p>
      </div>

      {/* Introduction Section */}
      <div className="container kubernetes-introduction">
        <div className="row">
          <div className="col-md-6">
            <h2 className="kubernetes-animate">{kubernetesData.introduction.heading}</h2>
            {kubernetesData.introduction.paragraphs.map((paragraph, index) => (
              <p key={index} className="kubernetes-animate">
                {paragraph}
              </p>
            ))}
          </div>
          <div className="col-md-6 kubernetes-image-container">
            <img
              src={kubernetesImage}
              className="img-fluid kubernetes-animate"
              alt="Kubernetes Setup Solutions"
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="container kubernetes-features">
        <div className="row">
          <div className="col-md-6 kubernetes-image-container">
            <img
              src={kubernetesHero}
              className="img-fluid kubernetes-animate"
              alt="Kubernetes Features"
              style={{ maxWidth: "100%" }}
            />
          </div>
          <div className="col-md-6">
            <h2 className="kubernetes-animate">{kubernetesData.features.heading}</h2>
            <ul>
              {kubernetesData.features.items.map((item, index) => (
                <li key={index} className="kubernetes-animate">
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div
        className="container-fluid kubernetes-benefits"
        style={{ backgroundImage: `url(${featuresImage})` }}
      >
        <div className="overlay">
          <h2 className="kubernetes-animate">{kubernetesData.benefits.heading}</h2>
          <div className="benefits-cards">
            {kubernetesData.benefits.items.map((item, index) => (
              <div key={index} className="benefit-card kubernetes-animate">
                <i className={item.icon}></i>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      {/* <div className="container kubernetes-cta">
        <h2 className="kubernetes-animate">{kubernetesData.cta.heading}</h2>
        <p className="kubernetes-animate">{kubernetesData.cta.text}</p>
        <button
          className="btn btn-primary kubernetes-animate"
          onClick={handleContactClick} // Add onClick handler
        >
          Contact Us
        </button>
      </div> */}
    </div>
  );
};